import * as React from 'react';
import { cn } from '@antimatterhq/ui/lib/utils';
import { Button } from '../button/button';

export interface DrawerRootProps extends React.HTMLAttributes<HTMLDivElement> {
  className?: string;
}

const DrawerRoot = React.forwardRef<HTMLDivElement, DrawerRootProps>(
  ({ className, children, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'fixed bottom-0 left-0 z-drawer w-full border-t border-grey-200 bg-sidebar shadow-lg transition-all dark:border-grey-650 dark:bg-sidebar',
        className,
      )}
      {...props}
    >
      {children}
    </div>
  ),
);
DrawerRoot.displayName = 'Drawer.Root';

export interface DrawerToggleProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  collapsed: boolean;
  onToggle: () => void;
}

const DrawerToggle = ({
  collapsed,
  onToggle,
  className,
  ...props
}: DrawerToggleProps) => (
  <Button
    aria-label={collapsed ? 'Expand drawer' : 'Collapse drawer'}
    onClick={onToggle}
    variant="ghost"
    size="small"
    iconOnly
    leftIcon={collapsed ? 'lucide:chevron-down' : 'lucide:chevron-up'}
    className={cn(
      'mr-3 mt-1 rounded p-1 transition-colors hover:bg-grey-100 dark:hover:bg-grey-700',
      className,
    )}
    {...props}
  />
);

DrawerToggle.displayName = 'Drawer.Toggle';

export interface DrawerCloseProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  className?: string;
  onClose: () => void;
  disabled?: boolean;
}

const DrawerClose = ({
  className,
  onClose,
  disabled,
  ...props
}: DrawerCloseProps) => (
  <Button
    variant="ghost"
    size="small"
    iconOnly
    leftIcon="lucide:x"
    className={cn(className)}
    {...props}
    onClick={onClose}
    disabled={disabled}
  />
);

export interface DrawerContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  collapsed: boolean;
}

const DrawerContent = ({
  collapsed,
  className,
  children,
  ...props
}: DrawerContentProps) =>
  !collapsed ? (
    <div className={cn('w-full flex-1', className)} {...props}>
      {children}
    </div>
  ) : null;

DrawerContent.displayName = 'Drawer.Content';

export interface DrawerCollapsedContentProps
  extends React.HTMLAttributes<HTMLDivElement> {
  collapsed: boolean;
}

const DrawerCollapsedContent = ({
  collapsed,
  className,
  children,
  ...props
}: DrawerCollapsedContentProps) =>
  collapsed ? (
    <div className={cn('w-full flex-1', className)} {...props}>
      {children}
    </div>
  ) : null;

DrawerCollapsedContent.displayName = 'Drawer.CollapsedContent';

export const Drawer = {
  Root: DrawerRoot,
  Toggle: DrawerToggle,
  Content: DrawerContent,
  CollapsedContent: DrawerCollapsedContent,
  Close: DrawerClose,
};
